import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, ReferenceLine, ResponsiveContainer, Tooltip } from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import confetti from "canvas-confetti";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Lock, ShieldCheck, Sparkles, Download, Award, Check, TrendingUp, TrendingDown, Minus, BadgeCheck, Share2, Copy, Mail, Trophy, Image, X } from "lucide-react";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const PAYPAL_CURRENCY = process.env.REACT_APP_PAYPAL_CURRENCY || "USD";

const TIERS = [
  { id: "standard", price: "A$9.99", label: "Standard Report", features: ["Exact IQ score", "Global percentile rank", "Bell-curve positioning"], highlight: false },
  { id: "premium", price: "A$19.99", label: "Premium Report", features: ["Everything in Standard", "5 cognitive sub-scores", "Downloadable PDF certificate"], highlight: true },
];

function bellData(userIq) {
  const pts = [];
  for (let iq = 55; iq <= 145; iq += 2) {
    const z = (iq - 100) / 15;
    const y = Math.exp(-0.5 * z * z);
    pts.push({ iq, y: +y.toFixed(4) });
  }
  return pts;
}

const Gauge = ({ iq }) => {
  const pct = Math.max(0, Math.min(1, (iq - 70) / 80));
  const R = 70, C = 2 * Math.PI * R;
  return (
    <div className="relative w-52 h-52">
      <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
        <circle cx="80" cy="80" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" />
        <circle cx="80" cy="80" r={R} fill="none" stroke="url(#g)" strokeWidth="12" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - pct)} />
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#06B6D4" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-sub font-black text-5xl text-white" data-testid="iq-score-value">{iq}</span>
        <span className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">IQ Score</span>
      </div>
    </div>
  );
};

const ordinal = (n) => {
  const v = Math.round(n);
  const s = ["th", "st", "nd", "rd"], m = v % 100;
  return v + (s[(m - 20) % 10] || s[m] || s[0]);
};

const InsightCard = ({ insight }) => {
  if (!insight) return null;
  if (!insight.has_previous) {
    return (
      <div data-testid="insight-card" className="mt-6 p-4 rounded-2xl bg-[#131B2A] border border-white/10 text-sm text-slate-400 fade-up">
        This is your first assessment — take it again anytime to track your improvement.
      </div>
    );
  }
  const hasIq = typeof insight.delta_iq === "number";
  const d = hasIq ? insight.delta_iq : insight.delta_percentile;
  const up = d > 0, flat = d === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const color = flat ? "text-slate-400" : up ? "text-emerald-400" : "text-rose-400";
  const unit = hasIq ? " IQ pts" : " percentile points";
  const sign = d > 0 ? "+" : "";
  return (
    <div data-testid="insight-card" className="mt-6 p-5 rounded-2xl bg-[#131B2A] border border-white/10 flex items-center gap-4 fade-up">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${flat ? "bg-slate-500/15" : up ? "bg-emerald-500/15" : "bg-rose-500/15"}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="flex-1">
        <div className="font-semibold text-slate-100">
          {flat ? "Same as your last attempt" : up ? "You improved!" : "Down from last time"}
        </div>
        <div className="text-sm text-slate-400 mt-0.5">
          <span className={`font-mono-data font-semibold ${color}`}>{sign}{d}{unit}</span>
          {hasIq
            ? <> vs your previous IQ {insight.prev_iq}</>
            : <> vs your previous {ordinal(insight.prev_percentile)} percentile</>}
        </div>
      </div>
    </div>
  );
};

export const Results = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payTier, setPayTier] = useState(null);
  const [payError, setPayError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [rank, setRank] = useState(null);
  const certRef = useRef(null);
  const firedConfetti = useRef(false);

  const load = () => {
    api.get(`/test/attempt/${attemptId}`).then((res) => {
      setData(res.data);
      setOptIn(!!res.data.leaderboard);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  };

  useEffect(() => { load(); }, [attemptId]);

  const fetchRank = () => {
    api.get("/leaderboard").then((res) => setRank(res.data)).catch(() => {});
  };

  useEffect(() => {
    if (data?.tier === "premium" && data?.leaderboard) fetchRank();
  }, [data]);

  const toggleOptIn = async () => {
    const next = !optIn;
    setOptIn(next);
    try {
      await api.post("/leaderboard/opt-in", { attempt_id: attemptId, opt_in: next });
      if (next) fetchRank(); else setRank(null);
    } catch (e) {
      setOptIn(!next);
    }
  };

  useEffect(() => {
    if (data?.paid && !firedConfetti.current) {
      firedConfetti.current = true;
      confetti({ particleCount: 140, spread: 80, origin: { y: 0.3 }, colors: ["#06B6D4", "#6366F1", "#F59E0B"] });
    }
  }, [data]);

  const openCheckout = (tier) => {
    setPayError(null);
    setPayTier(tier);
  };

  const createPayPalOrder = () =>
    api.post("/payments/paypal/create-order", { attempt_id: attemptId, tier: payTier, origin_url: window.location.origin })
      .then((res) => res.data.order_id)
      .catch((e) => {
        setPayError(e?.response?.data?.detail || "Could not start PayPal checkout.");
        throw e;
      });

  const onPayPalApprove = (data) =>
    api.post(`/payments/paypal/capture/${data.orderID}`)
      .then(() => { setPayTier(null); load(); })
      .catch((e) => setPayError(e?.response?.data?.detail || "We could not confirm your payment."));

  const downloadCertificate = async () => {
    if (!certRef.current) return;
    const canvas = await html2canvas(certRef.current, { scale: 2, backgroundColor: "#FCFBF7" });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height] });
    pdf.addImage(img, "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save("cognitive-metrics-certificate.pdf");
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0B0F17]"><Header /><div className="flex items-center justify-center h-[70vh] text-slate-400 font-mono-data text-sm">Loading results…</div></div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0B0F17]"><Header /><div className="flex items-center justify-center h-[70vh] text-slate-400">Attempt not found.</div></div>;
  }

  // ---------- PAYWALL ----------
  if (!data.paid) {
    return (
      <div className="min-h-screen bg-[#0B0F17] grain relative">
        <Header />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-[120px]" />
        <div className="relative max-w-4xl mx-auto px-5 py-12">
          <div className="text-center fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 mb-5">
              <Check className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">Assessment complete</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Your results are ready</h1>
            <p className="mt-3 text-slate-400">You scored in the <span className="text-cyan-300 font-semibold">{data.teaser.band}</span> — placing you around the <span className="text-amber-300 font-semibold">{ordinal(data.teaser.percentile)} percentile</span>.</p>
          </div>

          <InsightCard insight={data.insight} />

          {/* blurred teaser */}
          <div className="relative mt-9 rounded-3xl border border-white/10 bg-[#131B2A] p-10 flex flex-col items-center overflow-hidden fade-up">
            <div className="blur-lock flex flex-col items-center">
              <span className="font-sub font-black text-7xl text-white">1XX</span>
              <span className="text-slate-400 mt-2">Full cognitive breakdown across 5 dimensions</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-amber-400" />
                </div>
                <span className="text-sm text-slate-300">Unlock to reveal your exact score</span>
              </div>
            </div>
          </div>

          {/* tiers */}
          <div className="grid sm:grid-cols-2 gap-5 mt-8">
            {TIERS.map((t) => (
              <div
                key={t.id}
                className={`relative rounded-3xl p-7 border-2 transition-all ${t.highlight ? "border-amber-500/50 bg-gradient-to-b from-[#182234] to-[#0F172A] shadow-[0_0_50px_rgba(245,158,11,0.12)]" : "border-white/10 bg-[#131B2A]"}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Most popular
                  </div>
                )}
                <div className="font-semibold text-slate-200">{t.label}</div>
                <div className="font-sub font-black text-4xl text-white mt-2">{t.price}</div>
                <ul className="mt-5 space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                      <Check className={`w-4 h-4 ${t.highlight ? "text-amber-400" : "text-cyan-400"}`} /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`checkout-${t.id}-button`}
                  onClick={() => openCheckout(t.id)}
                  className={`mt-7 w-full py-3.5 rounded-xl font-bold transition-all ${t.highlight ? "bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]" : "bg-gradient-to-r from-cyan-500 to-indigo-500 text-white hover:shadow-[0_0_30px_rgba(6,182,212,0.4)]"}`}
                >
                  Unlock {t.label}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Secure PayPal checkout</span>
            <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-cyan-400" /> Instant unlock</span>
          </div>
        </div>

        {payTier && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" data-testid="paypal-modal">
            <div className="w-full max-w-md rounded-3xl bg-[#131B2A] border border-white/10 p-6 relative">
              <button data-testid="paypal-close-button" onClick={() => setPayTier(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
              <div className="font-semibold text-white text-lg">{TIERS.find((t) => t.id === payTier)?.label}</div>
              <div className="font-sub font-black text-3xl text-white mt-1">{TIERS.find((t) => t.id === payTier)?.price}</div>
              <p className="text-sm text-slate-400 mt-2 mb-5">Pay securely with PayPal or a debit/credit card.</p>
              {payError && <p className="text-sm text-rose-400 mb-3" data-testid="paypal-error">{payError}</p>}
              {PAYPAL_CLIENT_ID ? (
                <div data-testid="paypal-buttons">
                  <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: PAYPAL_CURRENCY, intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
                      forceReRender={[payTier]}
                      createOrder={createPayPalOrder}
                      onApprove={onPayPalApprove}
                      onError={() => setPayError("Payment could not be completed. Please try again.")}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <p className="text-sm text-amber-300" data-testid="paypal-not-configured">
                  PayPal isn't configured yet. Please try again shortly.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- UNLOCKED ----------
  const r = data.result;
  const chart = bellData(r.iq);
  return (
    <div className="min-h-screen bg-[#0B0F17] grain relative">
      <Header />
      <div className="relative max-w-5xl mx-auto px-5 py-12">
        <div className="text-center fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 mb-5">
            <Award className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">{data.tier === "premium" ? "Premium" : "Standard"} report</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Your cognitive profile</h1>
        </div>

        <InsightCard insight={data.insight} />

        <div className="grid lg:grid-cols-2 gap-6 mt-10 items-center">
          <div className="flex flex-col items-center p-8 rounded-3xl bg-[#131B2A] border border-white/10 fade-up">
            <Gauge iq={r.iq} />
            <div className="mt-4 text-center">
              <div className="text-lg font-semibold text-cyan-300">{r.band}</div>
              <div className="text-sm text-slate-400 mt-1" data-testid="percentile-value">Smarter than {r.percentile}% of test-takers</div>
              <div className="text-xs text-slate-500 mt-1 font-mono-data">{r.correct}/{r.total} correct</div>
            </div>
          </div>

          <div className="p-6 rounded-3xl bg-[#131B2A] border border-white/10 fade-up" style={{ animationDelay: "0.1s" }}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-cyan-400" />
              <span className="font-semibold text-slate-200">Where you fall on the curve</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="bell" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="iq" tick={{ fill: "#64748B", fontSize: 11 }} ticks={[70, 85, 100, 115, 130]} />
                <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} labelFormatter={(v) => `IQ ${v}`} formatter={() => ["", ""]} />
                <Area type="monotone" dataKey="y" stroke="#06B6D4" strokeWidth={2} fill="url(#bell)" />
                <ReferenceLine x={r.iq} stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 3" label={{ value: `You · ${r.iq}`, fill: "#F59E0B", fontSize: 12, position: "top" }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* premium sub-scores */}
        {data.tier === "premium" && r.breakdown && (
          <div className="mt-6 p-6 rounded-3xl bg-[#131B2A] border border-white/10 fade-up" data-testid="breakdown-section">
            <div className="font-semibold text-slate-200 mb-5">Cognitive dimensions</div>
            <div className="space-y-4">
              {r.breakdown.map((b) => (
                <div key={b.key}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-slate-300">{b.label}</span>
                    <span className="font-mono-data text-cyan-300">{b.correct}/{b.total} · {b.percent}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-full transition-all duration-700" style={{ width: `${b.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* certificate */}
        {data.tier === "premium" && (
          <div className="mt-8 fade-up">
            <div className="flex items-center justify-between mb-4">
              <span className="font-semibold text-slate-200">Official certificate</span>
              <button
                data-testid="download-certificate-button"
                onClick={downloadCertificate}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 font-bold hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all"
              >
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>
            <div className="overflow-x-auto">
              <div ref={certRef} className="mx-auto" style={{ width: 720 }}>
                <div className="relative bg-[#FCFBF7] text-slate-900 border-[10px] border-[#D4AF37] rounded-sm p-10 text-center">
                  <div className="text-[11px] uppercase tracking-[0.4em] text-[#B8962E] font-semibold">The Cognitive Metrics</div>
                  <div className="font-sub font-black text-3xl mt-4 text-slate-900">Certificate of Achievement</div>
                  <div className="w-24 h-0.5 bg-[#D4AF37] mx-auto my-5" />
                  <div className="text-sm text-slate-600">This certifies that the holder completed the credited</div>
                  <div className="text-sm text-slate-600">30-item intelligence assessment and achieved an</div>
                  <div className="font-sub font-black text-6xl text-slate-900 my-5">IQ {r.iq}</div>
                  <div className="text-sm text-slate-600">ranking in the <b>{r.band}</b> band —</div>
                  <div className="text-sm text-slate-600">the <b>{r.percentile}th</b> global percentile.</div>
                  <div className="flex items-center justify-center gap-2 mt-7">
                    <Award className="w-6 h-6 text-[#D4AF37]" />
                    <span className="font-mono-data text-xs text-slate-500">Verified · {new Date(data.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* email confirmation note */}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400 fade-up">
          <Mail className="w-4 h-4 text-cyan-400" />
          A copy of your results{data.tier === "premium" ? " and certificate" : ""} has been emailed to you.
        </div>

        {/* shareable verification badge (premium) */}
        {data.tier === "premium" && data.share_id && (
          <div className="mt-6 p-6 rounded-3xl bg-[#131B2A] border border-white/10 fade-up" data-testid="share-section">
            <div className="flex items-center gap-2 mb-3">
              <Share2 className="w-4 h-4 text-cyan-400" />
              <span className="font-semibold text-slate-200">Share your verified result</span>
            </div>
            <p className="text-sm text-slate-400 mb-4">Paste this link on LinkedIn or X for a rich preview card — or download the image to post anywhere. No login needed to verify.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="share-link-input"
                readOnly
                value={`${window.location.origin}/api/share/${data.share_id}`}
                className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-[#0B0F17] border border-white/10 text-slate-300 font-mono-data text-base sm:text-sm"
              />
              <button
                data-testid="copy-share-link-button"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/api/share/${data.share_id}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all"
              >
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
              </button>
            </div>
            <a
              data-testid="download-badge-link"
              href={`${window.location.origin}/api/public/badge/${data.share_id}.png`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors"
            >
              <Image className="w-4 h-4" /> Download shareable image
            </a>
          </div>
        )}

        {/* leaderboard opt-in (premium) */}
        {data.tier === "premium" && (
          <div className="mt-6 p-6 rounded-3xl bg-[#131B2A] border border-white/10 fade-up" data-testid="leaderboard-optin-section">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                <Trophy className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-200">Global leaderboard</div>
                <p className="text-sm text-slate-400 mt-1">
                  Compare your score anonymously against other premium test-takers.
                  {optIn && rank && rank.you && <span className="text-cyan-300"> You're ranked #{rank.you.rank} of {rank.total}.</span>}
                </p>
              </div>
              <button
                data-testid="leaderboard-toggle"
                role="switch"
                aria-checked={optIn}
                onClick={toggleOptIn}
                className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${optIn ? "bg-cyan-500" : "bg-white/15"}`}
              >
                <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${optIn ? "translate-x-5" : ""}`} />
              </button>
            </div>
            {optIn && (
              <button
                data-testid="view-leaderboard-button"
                onClick={() => navigate("/leaderboard")}
                className="mt-4 text-sm text-amber-300 hover:underline"
              >
                View the leaderboard →
              </button>
            )}
          </div>
        )}

        <div className="flex justify-center mt-10">
          <button onClick={() => navigate("/history")} data-testid="view-history-button" className="text-sm text-slate-400 hover:text-cyan-400 transition-colors">View all my attempts →</button>
        </div>
      </div>
    </div>
  );
};
